import { useState } from 'react';
import { Upload, Button, Tooltip, Dropdown, Space, message } from 'antd';
import { BgColorsOutlined, ReloadOutlined, CameraOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { EntityImage } from './EntityImage';
import { invalidateEntityAvatar } from './EntityAvatar';
import { useApi } from '../../hooks/use_api';
import { useAuth } from '../../context/AuthContext';
import { useNodeInfo } from '../../hooks/use_node_info';
import { ACCEPTED_IMAGE_TYPES, precheckImage } from '../../utils/image_upload';
import { avatarSeed, avatarColors, randomSeed, randomPalette } from '../../utils/entity_avatar';

/**
 * The entity page's picture: EntityImage in the glass circle, plus — for
 * anyone who can edit the entity — two controls to reshape the generated
 * avatar: shuffle its colours, shuffle its shape. Each click saves the new
 * { seed, colors } on the entity (PUT /api/entities/{id} `avatar`), so the
 * picture is the same for everyone afterwards.
 *
 * Uploading a real image (POST/DELETE /api/entities/{id}/image) is behind
 * feature_real_pictures; on nodes with it on, a camera button joins the two.
 * While a real image is set the shuffle controls hide — they'd change
 * nothing visible — and "use generated picture" brings them back.
 */
function EntityImageEditor({ entity, canEdit, onChanged }) {
    const api = useApi();
    const { user } = useAuth();
    const { featureRealPictures } = useNodeInfo();
    const [busy, setBusy] = useState(false);

    const finish = async (promise, okText) => {
        setBusy(true);
        try {
            await promise;
            invalidateEntityAvatar(entity.id);
            await onChanged?.();
            if (okText) message.success(okText);
        } catch (err) {
            message.error(err?.message || 'Could not update picture');
        } finally {
            setBusy(false);
        }
    };

    // Keep whichever half the user didn't touch: the stored value if there is
    // one, else what the default rendering used — so the first shuffle of
    // colours doesn't also silently change the shape, and vice versa.
    const saveAvatar = avatar => finish(
        api.put(`/api/entities/${entity.id}`, { avatar }, { params: { user_id: user?.id } }),
    );
    const shuffleColors = () => saveAvatar({ seed: avatarSeed(entity), colors: randomPalette() });
    const shuffleShape = () => saveAvatar({ seed: randomSeed(), colors: avatarColors(entity) || randomPalette() });

    const handleUpload = file => {
        const formData = new FormData();
        formData.append('file', file);
        finish(
            precheckImage(file, 'Picture').then(() =>
                api.post(`/api/entities/${entity.id}/image`, formData, { params: { user_id: user?.id } })),
            'Picture updated',
        );
        return false; // stop antd's own auto-upload; we POST manually above
    };

    const handleRemove = () => finish(
        api.del(`/api/entities/${entity.id}/image`, { params: { user_id: user?.id } }),
        'Back to the generated picture',
    );

    const uploadMenu = {
        items: [
            { key: 'upload', icon: <UploadOutlined />, label: (
                <Upload accept={ACCEPTED_IMAGE_TYPES.join(',')} beforeUpload={handleUpload} showUploadList={false} disabled={busy}>
                    {entity.image_url ? 'Replace picture' : 'Upload a real picture'}
                </Upload>
            ) },
            entity.image_url && { key: 'remove', icon: <DeleteOutlined />, danger: true, label: 'Use generated picture', onClick: handleRemove },
        ].filter(Boolean),
    };

    const showShuffle = canEdit && !entity.image_url;

    return (
        <div className="entity-avatar-wrap">
            <div className="glass-panel entity-avatar">
                <EntityImage entity={entity} size={76} />
            </div>
            {canEdit && (
                <Space size={4} className="entity-avatar-controls">
                    {showShuffle && (
                        <Tooltip title="Shuffle colours">
                            <Button className="entity-avatar-btn" shape="circle" size="small" icon={<BgColorsOutlined />} onClick={shuffleColors} disabled={busy} aria-label="Shuffle colours" />
                        </Tooltip>
                    )}
                    {showShuffle && (
                        <Tooltip title="Shuffle shape">
                            <Button className="entity-avatar-btn" shape="circle" size="small" icon={<ReloadOutlined />} onClick={shuffleShape} disabled={busy} aria-label="Shuffle shape" />
                        </Tooltip>
                    )}
                    {featureRealPictures && (
                        <Dropdown menu={uploadMenu} trigger={['click']} disabled={busy}>
                            <Tooltip title="Real picture">
                                <Button className="entity-avatar-btn" shape="circle" size="small" icon={<CameraOutlined />} loading={busy} aria-label="Real picture" />
                            </Tooltip>
                        </Dropdown>
                    )}
                </Space>
            )}
        </div>
    );
}

export default EntityImageEditor;
